import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { Button } from "../../components/Button";
import { OnboardingLayout } from "./OnboardingLayout";

/** What the person chose here, handed to the sign-up form and on to registration. */
export interface SignupConsents {
  acceptedTerms: true;
  marketingEmailOptIn: boolean;
}

const CONSENT_KEY = "prepai.signup-consents";

/** The choices made on this screen, for the rest of sign-up. Null if not made yet. */
export function readConsents(): SignupConsents | null {
  try {
    const raw = sessionStorage.getItem(CONSENT_KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<SignupConsents>) : null;
    return parsed?.acceptedTerms === true
      ? { acceptedTerms: true, marketingEmailOptIn: parsed.marketingEmailOptIn === true }
      : null;
  } catch {
    return null;
  }
}

function saveConsents(consents: SignupConsents): void {
  try {
    sessionStorage.setItem(CONSENT_KEY, JSON.stringify(consents));
  } catch {
    /* Private mode: the choice still travels in navigation state. */
  }
}

function Checkbox({
  id,
  checked,
  onChange,
  children,
}: {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer items-start gap-3 text-[13.5px] leading-[1.5] text-text"
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 h-5 w-5 shrink-0 accent-[var(--color-accent)]"
      />
      <span>{children}</span>
    </label>
  );
}

/**
 * Before any personal data is asked for: agreement to the Privacy Policy and
 * Terms (required), and an optional opt-in to learning-opportunity emails.
 *
 * The opt-in starts unticked and is never needed to continue — signing up and
 * using Prep AI work the same either way. This first release is email only.
 */
export function ConsentScreen() {
  const navigate = useNavigate();
  const saved = readConsents();
  const [acceptedTerms, setAcceptedTerms] = useState(saved?.acceptedTerms === true);
  const [marketingEmailOptIn, setMarketingEmailOptIn] = useState(
    saved?.marketingEmailOptIn === true,
  );

  const next = () => {
    if (!acceptedTerms) return;
    const consents: SignupConsents = { acceptedTerms: true, marketingEmailOptIn };
    saveConsents(consents);
    navigate("/signup/details", { state: { consents } });
  };

  return (
    <OnboardingLayout
      title="Before you start"
      subtitle="Two quick choices, then you can create your account."
      action={
        <Button onClick={next} disabled={!acceptedTerms}>
          Continue
        </Button>
      }
      footer={
        <p className="m-0 text-center text-[13px] text-text-muted">
          Already have an account?{" "}
          <Link to="/login" className="font-semibold text-accent">
            Log in
          </Link>
        </p>
      }
    >
      <section className="flex flex-col gap-3 rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <h2 className="m-0 text-[14px] font-semibold">Privacy Policy and Terms</h2>
        <p className="m-0 text-[12.5px] leading-[1.55] text-text-muted">
          How Prep AI uses your information, and the terms you agree to by using it.
        </p>
        <Checkbox id="accept-terms" checked={acceptedTerms} onChange={setAcceptedTerms}>
          I have read and agree to the Prep AI{" "}
          <Link to="/privacy" className="font-semibold text-accent underline">
            Privacy Policy
          </Link>{" "}
          and{" "}
          <Link to="/terms" className="font-semibold text-accent underline">
            Terms of Service
          </Link>
          .
        </Checkbox>
        <p className="m-0 text-[12px] text-text-muted">Required to create an account.</p>
      </section>

      <section className="flex flex-col gap-3 rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <h2 className="m-0 text-[14px] font-semibold">
          Stay informed about learning opportunities
        </h2>
        <p className="m-0 text-[12.5px] leading-[1.55] text-text-muted">
          Get updates from Publica AI about upcoming training programmes, educational opportunities,
          workshops, and other learning resources that may interest you.
        </p>
        <Checkbox
          id="marketing-opt-in"
          checked={marketingEmailOptIn}
          onChange={setMarketingEmailOptIn}
        >
          Yes, I would like to receive learning opportunities and programme updates from Publica AI
          by email.
        </Checkbox>
        <p className="m-0 text-[12px] text-text-muted">
          Optional. You can unsubscribe at any time. Your choice will not affect your access to Prep
          AI.
        </p>
      </section>
    </OnboardingLayout>
  );
}
